import { flow } from 'fp-ts/function'
import { AnimatePresence, motion } from 'framer-motion'
import * as R from 'ramda'
import { pick } from 'ramda'
import { ChangeEvent, FocusEvent, useEffect } from 'react'
import { useController, useFormState } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import { ProtectedComponent, Skeleton } from '../../Skeleton'
import { InputSkeleton } from '../../Skeleton/InputSkeleton'
import { InputError } from './InputError'
import { InputIcon } from './InputIcon'
import { InputLabel } from './InputLabel'
import { InputProps, InputTypes } from './input.types'
import { useIsMobile } from '../../../hooks'

type OnChangeEvent = ChangeEvent<HTMLInputElement>
type OnBlurEvent = FocusEvent<HTMLInputElement, Element>

/**
 * Input Component That allows for easy, but complex validation
 *
 * @example
 * const exampleForm = () => {
 *  const form = useForm()
 *  const onSubmit = console.log
 *
 *  return (
 *    <form onSubmit={form.handleSubmit(onSubmit)}>
 *      // required field
 *      <Input form={form} name="Required Field" required />
 *      // number field with min/max validation
 *      <Input form={form} type="number" name="Number Field" min={3} max={5} />
 *      // text field with length validation
 *      <Input form={form} name="Text Field" minLength={3} maxLength={5} />
 *      // field with custom validation
 *      <Input form={form} name="Custom Validation"
 *        validate={{
 *           the key is the message shown if the current value of the field does not pass the predicate
 *          'Greater Than 3': currFieldValue => currFieldValue > 3,
 *          'Must Equal Test': currFieldValue => currFieldValue === 'test',
 *        }}
 *       />
 *    </form>
 *  )
 * }
 */
export function Input<T extends InputTypes = InputTypes>(
  inputProps: InputProps<T>,
) {
  const isMobile = useIsMobile()
  const {
    stopPropagation,
    label,
    name,
    type = 'text',
    autoFocus,
    autoComplete,
    ...props
  } = inputProps
  const { control, trigger, watch, setValue, ...form } = inputProps.form

  const { errors } = useFormState({ control, name })

  const validators = pick(
    ['min', 'max', 'minLength', 'maxLength', 'required', 'pattern', 'validate'],
    props,
  )

  const { fieldState, field } = useController({
    name,
    control,
    rules: validators,
    disabled: props.disabled || form.formState.disabled,
    defaultValue: props.defaultValue,
  })

  const transformPrimitive = (value: any) => {
    // If type is number, remove any nonNumeric character and transform the string to a number
    if (type === 'number') return Number(value.replace(/\D/, ''))

    // If the type is text, we do nothing because the event.value is already a string
    if (type === 'text') return value

    // Otherwise, we just return a non transformed value
    return value
  }

  // If our props.transform function isn't provided, we use R.idenity, to passthrough
  const transform = props.transform || R.identity
  const parse = props.parser || R.identity
  const format = props.format || R.identity

  const transformInput = flow(transformPrimitive, transform, parse)

  const handleChange = (event: OnChangeEvent) => {
    const transformedInput = transformInput(event?.target?.value)

    // Modify the form value
    field.onChange(transformedInput)

    // If the field state is invalid, trigger validation on every change
    // If we don't do this, it will only trigger on blur(unless we set 'mode' to 'all' or 'onChange')
    if (fieldState.invalid) trigger(name)
    // else if (fieldState.isDirty && fieldState.isTouched) trigger(name)

    // If an onChange function is provided, run it with the triggered data
    if (props.onChange) props.onChange(transformedInput)

    return transformedInput
  }

  const handleBlur = (event: OnBlurEvent) => {
    // Trigger validation always
    trigger(name)

    field.onBlur() // Triggers Validation (if mode: "onBlur" or mode: "all" is Enabled):

    const transformedInput = transformInput(event?.target?.value)

    if (props.onBlur) props.onBlur(transformedInput)

    field.onChange(transformedInput) // This may not be needed, I'm not sure
  }

  useEffect(() => {
    if (type !== 'number') return
    const fieldValue = watch(name)
    if (!fieldValue) return
    if (typeof fieldValue === 'number') return
    setValue(name, Number(fieldValue))
  }, [watch(name)])

  const ignorePermissions =
    typeof props.hasPermission === 'undefined' || !!props.loading
  const iconPosition = props.iconPosition || 'left'

  const fieldContainerClasses = twMerge(
    'mt-6 relative transition-all duration-300 bg-transparent dark:bg-transparent',
    fieldState.invalid && 'pb-6',
    props.hint && 'pb-6',
    props.className,
  )

  const inputContainerClasses = twMerge(
    'z-10 relative group flex items-stretch bg-white/10',
    'rounded-lg border border-gray-300 shadow-sm',
    'focus-within:shadow-outline-blue focus-within:border-blue-300 overflow-hidden ',
    'transition duration-150 ease-in-out',
    props.icon && 'pl-2 sm:pl-1',
    fieldState.invalid &&
      'border-red-400 focus-within:shadow-outline-blue focus-within:border-red-400',
    'dark:text-base-500 dark:text-sm/6 dark:border-white/5 dark:focus-within:border dark:focus-within:border-gray-500',
    fieldState.invalid && 'dark:border dark:focus-within:border-red-800',
    !ignorePermissions &&
      !props.hasPermission &&
      'border-amber-300/60 bg-amber-50 border shadow-sm',
    props.inputClasses,
  )

  const inputElementClasses = twMerge(
    'block w-full pl-3 py-2 placeholder-gray-400 text-base-1000',
    'duration-150 ease-in-out transition',
    'outline-none appearance-none',
    'sm:text-sm sm:leading-5',
    'dark:border-none dark:bg-transparent dark:shadow-none dark:placeholder:text-base-500',
    'disabled:bg-base-200 disabled:text-base-700',
    !props.hasPermission &&
      'disabled:bg-amber-50 opacity-85  disabled:text-gray-400',
    props.inputFieldClasses,
  )

  const inputErrorClasses = twMerge(
    'z-0 absolute bottom-0 z-0 left-0',
    'text-sm text-red-400 overflow-hidden dark:text-red-400',
    props.inputErrorClasses,
  )

  const inputHintClasses = twMerge(
    'z-0 text-xs z-0 absolute bottom-0 left-0 text-base-400 overflow-hidden',
    props.inputHintClasses,
  )

  const iconVariant = (() => {
    if (props.iconVariant) return props.iconVariant
    if (!props.iconClasses) return 'centered'
    return 'unstyled'
  })()

  return (
    <div className={fieldContainerClasses}>
      <InputLabel
        label={label}
        noLabel={props.noLabel}
        className={props.labelClasses}
        required={validators.required}
      />

      <ProtectedComponent
        onRequestPermission={props?.onRequestPermission}
        contentVisible
        hasPermission={props.hasPermission}
        ignorePermissionsIf={ignorePermissions}>
        <div className={inputContainerClasses}>
          <InputIcon
            hasPermission={props.hasPermission}
            variant={iconVariant}
            invalid={fieldState.invalid}
            className={twMerge(
              props.iconClasses,
              iconPosition === 'right' && 'hidden',
            )}
            icon={props.icon}
            loading={props.loading}
          />

          {!fieldState.invalid && props.prependElement && props.prependElement}

          <Skeleton
            loading={props.loading}
            skeleton={<InputSkeleton {...inputProps} />}>
            <input
              ref={field.ref}
              onClick={(e) => {
                if (stopPropagation) e.stopPropagation()
              }}
              value={format(field.value)}
              onChange={handleChange}
              onWheel={(e) => type === 'number' && e.currentTarget.blur()}
              onBlur={handleBlur}
              className={inputElementClasses}
              id={label}
              type={type}
              name={name}
              placeholder={(() => {
                if (isMobile && props.noLabel) return label
                return props.placeholder || label
              })()}
              autoFocus={autoFocus}
              autoCapitalize={props?.autoCapitalize}
              autoComplete={(() => {
                if (typeof autoComplete === 'string') return autoComplete
                if (!!autoComplete) return 'on'
                return 'off'
              })()}
              defaultValue={props.defaultValue}
              disabled={(() => {
                if (ignorePermissions) return field.disabled
                return !props.hasPermission
              })()}
              tabIndex={props.tabIndex}
            />
          </Skeleton>

          {props.appendElement && props.appendElement}

          {props?.iconPosition === 'right' && (
            <InputIcon
              iconPosition='right'
              variant={iconVariant}
              icon={props.icon}
              className={twMerge(
                props.iconClasses,
                iconPosition === 'left' && 'hidden',
                'pl-3 px-2',
              )}
            />
          )}
        </div>
      </ProtectedComponent>

      <AnimatePresence initial={false}>
        {props.hasPermission && props.hint && !errors[name] && (
          <motion.p
            initial={{ opacity: 0, translateY: '5px' }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0 }}
            className={inputHintClasses}>
            {props.hint}
          </motion.p>
        )}
      </AnimatePresence>

      <InputError
        fieldValue={field.value}
        error={fieldState.error}
        validators={validators}
        className={inputErrorClasses}
        format={props.format}
      />
    </div>
  )
}
